import { injectable } from 'inversify';
import type { UserRepository } from '../../domain/repositories/UserRepository';
import { User, UserRole, LoginCredentials, UserSession } from '../../domain/models';
import { databaseConfig } from '../../config/database';

/**
 * Implementation of UserRepository using Backend API
 */
@injectable()
export class UserRepositoryImpl implements UserRepository {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = databaseConfig.mongodb.apiBaseUrl;
  }

  async findById(id: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/users/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        return this.mapToUser(result.data || result);
      }
      return null;
    } catch (error) {
      console.error('Failed to find user by ID:', error);
      return null;
    }
  }

  async findByUsername(username: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/users?username=${username}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        const users = result.data || [];
        return users.length > 0 ? this.mapToUser(users[0]) : null;
      }
      return null;
    } catch (error) {
      console.error('Failed to find user by username:', error);
      return null;
    }
  }

  async authenticate(credentials: LoginCredentials): Promise<UserSession | null> {
    try {
      console.log(`🔐 Authenticating user '${credentials.username}' via backend API...`);
      
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          branchId: credentials.branchId,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        
        // Map backend response to UserSession
        const user = this.mapToUser(data.user);
        const userSession: UserSession = {
          user,
          token: {
            accessToken: data.token.accessToken,
            refreshToken: data.token.refreshToken,
            expiresAt: new Date(data.token.expiresAt),
          },
          branchId: data.branchId || credentials.branchId,
          loginTime: new Date(data.loginTime || Date.now()),
        };

        console.log(`✅ Authentication successful for user '${credentials.username}'`);
        return userSession;
      } else {
        const error = await response.json().catch(() => ({}));
        console.warn(`❌ Authentication failed: ${error.message || response.statusText}`);
        return null;
      }
    } catch (error) {
      console.error('❌ Authentication error:', error);
      return null;
    }
  }

  async updateLastLogin(userId: string): Promise<void> {
    // Last login is updated by the backend during authentication
    console.log(`Last login updated for user '${userId}'`);
  }

  async findByBranch(branchId: string): Promise<User[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/users?branchId=${branchId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        const users = result.data || [];
        return users.map((u: any) => this.mapToUser(u));
      }
      return [];
    } catch (error) {
      console.error('Failed to find users by branch:', error);
      return [];
    }
  }

  async validateToken(token: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return this.mapToUser(result.data || result);
      }
      return null;
    } catch (error) {
      console.error('Failed to validate token:', error);
      return null;
    }
  }

  async refreshToken(refreshToken: string): Promise<UserSession | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        
        const user = this.mapToUser(data.user);
        return {
          user,
          token: {
            accessToken: data.token.accessToken,
            refreshToken: data.token.refreshToken,
            expiresAt: new Date(data.token.expiresAt),
          },
          branchId: data.branchId || user.branchId,
          loginTime: new Date(),
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  }

  async invalidateSession(userId: string): Promise<void> {
    console.log(`Session invalidated for user '${userId}'`);
  }

  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    const response = await fetch(`${this.baseUrl}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (response.ok) {
      const result = await response.json();
      return this.mapToUser(result.data || result);
    }
    throw new Error('Failed to create user');
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await fetch(`${this.baseUrl}/api/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (response.ok) {
      const result = await response.json();
      return this.mapToUser(result.data || result);
    }
    throw new Error('Failed to update user');
  }

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/users/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Failed to delete user');
    }
  }

  /**
   * Map backend response to domain model
   */
  private mapToUser(data: any): User {
    return {
      id: data.visibleId || data.id || data._id,
      username: data.username,
      email: data.email,
      firstName: data.firstName || data.name?.split(' ')[0] || data.name || '',
      lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
      role: this.mapRole(data.role),
      branchId: data.branchId,
      isActive: data.isActive ?? true,
      createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
    };
  }

  private mapRole(role: string): UserRole {
    const roleMap: Record<string, UserRole> = {
      'admin': UserRole.ADMIN,
      'ADMIN': UserRole.ADMIN,
      'manager': UserRole.MANAGER,
      'MANAGER': UserRole.MANAGER,
      'cashier': UserRole.CASHIER,
      'CASHIER': UserRole.CASHIER,
    };
    return roleMap[role] || UserRole.CASHIER;
  }
}
